import { useCallback, useEffect, useState } from "react";
import { Check, Clipboard, Download, Loader2, RotateCcw, Send, X } from "lucide-react";
import { toast } from "sonner";

import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CLIENT_PROCESS_OPTIONS = [
  "submitted",
  "awaiting_feedback",
  "interview",
  "offer",
  "offer_accepted",
  "joined",
  "client_rejected",
  "candidate_withdrawn",
  "offer_declined",
  "on_hold",
];

const REVIEW_ACTIONS = [
  ["approve", "Approve", Check],
  ["return_for_changes", "Return for Changes", RotateCcw],
  ["request_info", "Request Info", Clipboard],
  ["decline", "Decline", X],
];

export function SubmissionReviewDialog({ submissionId, open, onClose, onChanged }) {
  const { can, user } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [packageData, setPackageData] = useState(null);
  const [packageOpen, setPackageOpen] = useState(false);
  const [delivery, setDelivery] = useState({ recipient: "", subject: "", message: "", note: "" });
  const [feedback, setFeedback] = useState({
    feedback: "",
    action_needed: "",
    due_date: "",
    process_status: "awaiting_feedback",
  });
  const [readiness, setReadiness] = useState(null);
  const [resubmit, setResubmit] = useState(null);

  const canManage = can("job:edit") || user?.role === "bdm";
  const isRecruiterOwner = user?.id && submission?.recruiter_id === user.id;

  const load = useCallback(async () => {
    if (!submissionId || !open) {
      return;
    }
    setSubmission(null);
    setPackageOpen(false);
    setPackageData(null);
    try {
      const { data } = await api.get(`/submissions/${submissionId}/review`);
      setSubmission(data);
    } catch (error) {
      toast.error(apiErr(error));
      onClose?.();
    }
  }, [onClose, open, submissionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!submission || !isRecruiterOwner) {
      return;
    }
    if (
      !["draft", "information_requested", "returned_for_changes"].includes(
        submission.internal_review_status,
      )
    ) {
      return;
    }
    api
      .get("/submissions/internal/readiness", {
        params: { candidate_id: submission.candidate_id, job_id: submission.job_id },
      })
      .then(({ data }) => {
        setReadiness(data);
        setResubmit({
          resume_document_id: data.resume_versions?.[0]?.document_id || "",
          summary_version_id: data.summary_versions?.[0]?.id || "",
          screening_version_id: data.screening_versions?.[0]?.id || "",
          salary_confirmation: submission.salary_confirmation || "",
          availability_confirmation: submission.availability_confirmation || "",
          submitted_comp: submission.submitted_comp || "",
        });
      })
      .catch((error) => toast.error(apiErr(error)));
  }, [isRecruiterOwner, submission]);

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const review = async (action) => {
    if (action !== "approve" && !reason.trim()) {
      toast.error("A reason is required for this action");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${submission.id}/review-action`, {
        action,
        reason: reason.trim() || null,
      });
      toast.success("Review action recorded");
      setReason("");
      await refresh();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const openPackage = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/submissions/${submission.id}/client-package`);
      setPackageData(data);
      setDelivery({
        recipient: "",
        subject: data.default_subject || "",
        message: data.default_message || "",
        note: "",
      });
      setPackageOpen(true);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const downloadPackage = () => {
    const text = [delivery.subject, "", delivery.message].join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `nexreq-client-package-${submission.id}.txt`;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const copyPackage = async () => {
    try {
      await navigator.clipboard.writeText(`${delivery.subject}\n\n${delivery.message}`);
      toast.success("Client package copied");
    } catch {
      toast.error("Copy is unavailable in this browser");
    }
  };

  const markSent = async () => {
    if (
      !delivery.recipient.trim() ||
      !delivery.subject.trim() ||
      !delivery.message.trim() ||
      !delivery.note.trim()
    ) {
      toast.error("Recipient, subject, message, and note are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${submission.id}/client-delivery/mark-sent`, {
        recipient: delivery.recipient,
        subject: delivery.subject,
        message: delivery.message,
        attachments: packageData.attachments.map((item) => item.document_id),
        note: delivery.note,
      });
      toast.success("Client delivery recorded");
      await refresh();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedback.feedback || !feedback.action_needed || !feedback.due_date) {
      toast.error("Feedback, action needed, and due date are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${submission.id}/client-feedback`, feedback);
      toast.success("Feedback logged and recruiter task created");
      setFeedback({
        feedback: "",
        action_needed: "",
        due_date: "",
        process_status: submission.client_process_status || "awaiting_feedback",
      });
      await refresh();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const resubmitForReview = async () => {
    if (!resubmit) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${submission.id}/submit-for-review`, {
        candidate_id: submission.candidate_id,
        job_id: submission.job_id,
        ...resubmit,
        submitted_comp: resubmit.submitted_comp ? Number(resubmit.submitted_comp) : null,
      });
      toast.success("Resubmitted for BDM review");
      await refresh();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  if (!submission) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
        <DialogContent data-testid="submission-review-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto sm:max-w-6xl"
        data-testid="submission-review-dialog"
      >
        <DialogHeader>
          <DialogTitle>{submission.candidate_name} — Internal Submission Review</DialogTitle>
          <DialogDescription data-testid="submission-review-status">
            Internal: {display(submission.internal_review_status)} · Client:{" "}
            {display(submission.client_process_status)}
            {submission.review_cycle ? ` · Attempt ${submission.review_cycle}` : ""}
          </DialogDescription>
        </DialogHeader>

        {!packageOpen ? (
          <div className="space-y-5 py-2">
            <div className="grid gap-4 lg:grid-cols-2">
              <ReviewSection title="Candidate" testId="submission-review-candidate">
                <Detail
                  label="Candidate"
                  value={submission.candidate_snapshot?.full_name || submission.candidate_name}
                />
                <Detail
                  label="Current title"
                  value={submission.candidate_snapshot?.current_title}
                />
                <Detail
                  label="Experience"
                  value={withYears(submission.candidate_snapshot?.years_experience)}
                />
              </ReviewSection>
              <ReviewSection title="Resume" testId="submission-review-resume">
                <Detail
                  label="Selected version"
                  value={`v${submission.resume_snapshot?.version || "—"}`}
                />
                <Detail label="File" value={submission.resume_snapshot?.filename} />
                {submission.resume_snapshot?.document_id && (
                  <a
                    className="mt-2 inline-block text-sm text-[#255058] underline"
                    href={
                      `${API}/candidates/${submission.candidate_id}/resume/versions/` +
                      `${submission.resume_snapshot.document_id}/download`
                    }
                    target="_blank"
                    rel="noreferrer"
                    data-testid="submission-review-resume-open"
                  >
                    Open selected resume
                  </a>
                )}
              </ReviewSection>
              <ReviewSection title="Job" testId="submission-review-job">
                <Detail label="Role" value={submission.job_title} />
                <Detail label="Client" value={submission.client_name} />
                <Detail label="Region" value={submission.region} />
              </ReviewSection>
              <ReviewSection title="Screening" testId="submission-review-screening">
                <Detail
                  label="Selected version"
                  value={`v${submission.screening_snapshot?.version || "—"}`}
                />
                <Detail label="Notes" value={submission.screening_snapshot?.notes} />
                <Detail
                  label="Answers"
                  value={`${submission.screening_snapshot?.answers?.length || 0} recorded`}
                />
              </ReviewSection>
              <ReviewSection title="Salary" testId="submission-review-salary">
                <Detail label="Expectation" value={salaryText(submission.candidate_snapshot)} />
                <Detail label="Status" value={display(submission.salary_confirmation)} />
              </ReviewSection>
              <ReviewSection title="AI" testId="submission-review-ai">
                <Detail
                  label="Analysis"
                  value={
                    submission.ai_snapshot ? "Saved AI analysis attached" : "No saved AI analysis"
                  }
                />
              </ReviewSection>
              <ReviewSection title="Summary" testId="submission-review-summary">
                <Detail
                  label="Selected version"
                  value={`v${submission.summary_snapshot?.version || "—"}`}
                />
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                  {submission.summary_snapshot?.text || "No summary snapshot"}
                </p>
              </ReviewSection>
              <ReviewSection title="History" testId="submission-review-history">
                <History items={submission.internal_review_history} />
                <History items={submission.client_process_history} />
              </ReviewSection>
            </div>

            {canManage && submission.internal_review_status === "submitted_for_review" && (
              <Card className="p-4" data-testid="submission-review-actions">
                <Label htmlFor="submission-review-reason">Reason</Label>
                <Textarea
                  id="submission-review-reason"
                  className="mt-2"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Required for every action except approval"
                  data-testid="submission-review-reason"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {REVIEW_ACTIONS.map(([action, label, Icon]) => (
                    <Button
                      key={action}
                      variant={action === "approve" ? "default" : "outline"}
                      className={
                        action === "approve" ? "bg-[#255058] text-white hover:bg-[#1c3d43]" : ""
                      }
                      onClick={() => review(action)}
                      disabled={busy}
                      data-testid={`submission-review-${action}`}
                    >
                      <Icon className="mr-1.5 h-4 w-4" /> {label}
                    </Button>
                  ))}
                </div>
              </Card>
            )}

            {isRecruiterOwner && readiness && resubmit && (
              <ResubmitPanel
                readiness={readiness}
                values={resubmit}
                onChange={setResubmit}
                onResubmit={resubmitForReview}
                busy={busy}
              />
            )}

            {canManage && submission.internal_review_status === "approved" && (
              <div className="flex flex-wrap gap-2">
                <Button
                  className="bg-[#255058] text-white hover:bg-[#1c3d43]"
                  onClick={openPackage}
                  disabled={busy}
                  data-testid="submission-open-client-package"
                >
                  <Send className="mr-1.5 h-4 w-4" /> Prepare Client Package
                </Button>
              </div>
            )}

            {canManage && submission.client_process_status !== "not_submitted" && (
              <ClientFeedbackPanel
                feedback={feedback}
                onChange={setFeedback}
                onSubmit={submitFeedback}
                busy={busy}
              />
            )}
          </div>
        ) : (
          <ClientPackagePanel
            submission={submission}
            packageData={packageData}
            delivery={delivery}
            onChange={setDelivery}
            onBack={() => setPackageOpen(false)}
            onCopy={copyPackage}
            onDownload={downloadPackage}
            onMarkSent={markSent}
            busy={busy}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="submission-review-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewSection({ title, testId, children }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="font-display text-sm font-semibold">{title}</h3>
      <div className="mt-3 space-y-2">{children}</div>
    </Card>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "Not recorded"}</span>
    </div>
  );
}

function History({ items = [] }) {
  return (
    <div className="space-y-1.5">
      {items
        .slice()
        .reverse()
        .map((item, index) => (
          <div key={`${item.at}-${index}`} className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{display(item.status)}</span> ·{" "}
            {item.by || "System"}
            {item.note ? ` · ${item.note}` : ""}
          </div>
        ))}
    </div>
  );
}

function ResubmitPanel({ readiness, values, onChange, onResubmit, busy }) {
  return (
    <Card className="p-4" data-testid="submission-resubmit-panel">
      <h3 className="font-display text-sm font-semibold">Correct and resubmit</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <VersionSelect
          label="Resume version"
          value={values.resume_document_id}
          options={readiness.resume_versions || []}
          valueKey="document_id"
          testId="submission-resubmit-resume"
          onChange={(value) => onChange({ ...values, resume_document_id: value })}
        />
        <VersionSelect
          label="Summary version"
          value={values.summary_version_id}
          options={readiness.summary_versions || []}
          valueKey="id"
          testId="submission-resubmit-summary"
          onChange={(value) => onChange({ ...values, summary_version_id: value })}
        />
        <VersionSelect
          label="Screening version"
          value={values.screening_version_id}
          options={readiness.screening_versions || []}
          valueKey="id"
          testId="submission-resubmit-screening"
          onChange={(value) => onChange({ ...values, screening_version_id: value })}
        />
        <StatusSelect
          label="Salary status"
          value={values.salary_confirmation}
          testId="submission-resubmit-salary"
          onChange={(value) => onChange({ ...values, salary_confirmation: value })}
        />
        <StatusSelect
          label="Availability status"
          value={values.availability_confirmation}
          testId="submission-resubmit-availability"
          onChange={(value) => onChange({ ...values, availability_confirmation: value })}
        />
      </div>
      <Button
        className="mt-4 bg-[#255058] text-white hover:bg-[#1c3d43]"
        onClick={onResubmit}
        disabled={busy}
        data-testid="submission-resubmit-button"
      >
        <RotateCcw className="mr-1.5 h-4 w-4" /> Resubmit for Review
      </Button>
    </Card>
  );
}

function VersionSelect({ label, value, options, valueKey, testId, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={testId}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((item) => (
            <SelectItem key={item[valueKey]} value={item[valueKey]}>
              v{item.version || 1}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatusSelect({ label, value, testId, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={testId}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue placeholder="Select status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="confirmed">Confirmed</SelectItem>
          <SelectItem value="unconfirmed">Unconfirmed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ClientPackagePanel({
  submission,
  packageData,
  delivery,
  onChange,
  onBack,
  onCopy,
  onDownload,
  onMarkSent,
  busy,
}) {
  return (
    <div className="space-y-4 py-2" data-testid="client-package-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold">Client Package</h3>
          <p className="text-sm text-muted-foreground">
            Manual delivery mode — no provider email is claimed.
          </p>
        </div>
        <Button variant="outline" onClick={onBack} data-testid="client-package-back">
          Back to review
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="client-package-recipient">Recipient</Label>
          <Input
            id="client-package-recipient"
            value={delivery.recipient}
            onChange={(event) => onChange({ ...delivery, recipient: event.target.value })}
            data-testid="client-package-recipient"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-package-subject">Subject</Label>
          <Input
            id="client-package-subject"
            value={delivery.subject}
            onChange={(event) => onChange({ ...delivery, subject: event.target.value })}
            data-testid="client-package-subject"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="client-package-message">Message</Label>
        <Textarea
          id="client-package-message"
          className="min-h-56"
          value={delivery.message}
          onChange={(event) => onChange({ ...delivery, message: event.target.value })}
          data-testid="client-package-message"
        />
      </div>
      <Card className="p-4" data-testid="client-package-attachments">
        <div className="text-sm font-medium">Attachments</div>
        {packageData.attachments.map((attachment) => (
          <div key={attachment.document_id} className="mt-2 text-sm text-muted-foreground">
            {attachment.label}: {attachment.filename}
          </div>
        ))}
      </Card>
      <div className="space-y-1.5">
        <Label htmlFor="client-package-note">Mark-as-sent note</Label>
        <Textarea
          id="client-package-note"
          value={delivery.note}
          onChange={(event) => onChange({ ...delivery, note: event.target.value })}
          placeholder="Required confirmation note"
          data-testid="client-package-note"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={onCopy} data-testid="client-package-copy">
          <Clipboard className="mr-1.5 h-4 w-4" /> Copy
        </Button>
        <Button variant="outline" onClick={onDownload} data-testid="client-package-download">
          <Download className="mr-1.5 h-4 w-4" /> Download
        </Button>
        <Button
          className="bg-[#255058] text-white hover:bg-[#1c3d43]"
          onClick={onMarkSent}
          disabled={busy}
          data-testid="client-package-mark-sent"
        >
          {busy ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-1.5 h-4 w-4" />
          )}
          Mark as Sent
        </Button>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="client-package-submission-id">
        Submission {submission.id}
      </div>
    </div>
  );
}

function ClientFeedbackPanel({ feedback, onChange, onSubmit, busy }) {
  return (
    <Card className="p-4" data-testid="submission-client-feedback">
      <h3 className="font-display text-sm font-semibold">Log client feedback</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="client-feedback-text">Feedback</Label>
          <Textarea
            id="client-feedback-text"
            value={feedback.feedback}
            onChange={(event) => onChange({ ...feedback, feedback: event.target.value })}
            data-testid="client-feedback-text"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-feedback-action">Action needed</Label>
          <Input
            id="client-feedback-action"
            value={feedback.action_needed}
            onChange={(event) => onChange({ ...feedback, action_needed: event.target.value })}
            data-testid="client-feedback-action"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-feedback-due-date">Due date</Label>
          <Input
            id="client-feedback-due-date"
            type="date"
            value={feedback.due_date}
            onChange={(event) => onChange({ ...feedback, due_date: event.target.value })}
            data-testid="client-feedback-due-date"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="client-feedback-process">Client process</Label>
          <Select
            value={feedback.process_status}
            onValueChange={(value) => onChange({ ...feedback, process_status: value })}
          >
            <SelectTrigger id="client-feedback-process" data-testid="client-feedback-process">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_PROCESS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {display(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button
        className="mt-4 bg-[#255058] text-white hover:bg-[#1c3d43]"
        onClick={onSubmit}
        disabled={busy}
        data-testid="client-feedback-submit"
      >
        Log Feedback & Create Task
      </Button>
    </Card>
  );
}

function display(value) {
  return String(value || "Not recorded").replaceAll("_", " ");
}

function withYears(value) {
  return value === null || value === undefined ? null : `${value} years`;
}

function salaryText(candidate = {}) {
  if (candidate.salary_expectation === null || candidate.salary_expectation === undefined) {
    return null;
  }
  return `${candidate.currency || "USD"} ${candidate.salary_expectation}`;
}
